import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import {
  deleteReplySignature,
  deleteTicketMailbox,
  deleteTicketStatusDefinition,
  deleteTicketTagDefinition,
  deleteSpamRule,
  fetchAllTicketStatusDefinitions,
  fetchAllTicketTagDefinitions,
  fetchAllTicketMailboxes,
  fetchSpamRules,
  fetchTicketEmailTemplate,
  fetchTicketNumberConfig,
  fetchTicketWorkflowSettings,
  listReplySignatures,
  saveReplySignature,
  saveTicketWorkflowSettings,
  setSpamRuleActive,
  upsertSpamRule,
  upsertTicketEmailTemplate,
  upsertTicketMailbox,
  upsertTicketStatusDefinition,
  upsertTicketTagDefinition,
  type ReplySignature,
} from '../../lib/tickets';
import { OpeningAckTemplateEditor } from './OpeningAckTemplateEditor';
import { Modal } from '../ui/Modal';
import { DEFAULT_CLOSING_ACK_INNER, toStoredOpeningAckBody, wrapOpeningAckBody } from '../../lib/openingAckTemplate';
import type {
  TicketMailbox,
  TicketSpamRule,
  TicketStatusDefinition,
  TicketTagDefinition,
} from '../../types/tickets';
import type { TicketPortalKind } from '../../lib/tickets';

const MemoBodyEditor = lazy(() =>
  import('../forms/MemoBodyEditor').then((m) => ({ default: m.MemoBodyEditor })),
);

export function TicketingAdminPanel({ embedded = false }: { embedded?: boolean }) {
  const [mailboxes, setMailboxes] = useState<TicketMailbox[]>([]);
  const [selectedMailbox, setSelectedMailbox] = useState<TicketMailbox | null>(null);
  const [tags, setTags] = useState<TicketTagDefinition[]>([]);
  const [statuses, setStatuses] = useState<TicketStatusDefinition[]>([]);
  const [spamRules, setSpamRules] = useState<TicketSpamRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [manageOpen, setManageOpen] = useState<'mailboxes' | 'tags' | 'statuses' | 'spam' | null>(null);
  const [templateKind, setTemplateKind] = useState<
    'opening_ack' | 'closing_ack' | 'follow_up_ack' | null
  >(null);
  const [closingSubject, setClosingSubject] = useState('[{{ticket_number}}] Your ticket has been closed');
  const [closingBody, setClosingBody] = useState(wrapOpeningAckBody(DEFAULT_CLOSING_ACK_INNER));
  const [closingActive, setClosingActive] = useState(false);
  const [followUpSubject, setFollowUpSubject] = useState(
    'Follow-up: {{subject}} [{{ticket_number}}]',
  );
  const [followUpBody, setFollowUpBody] = useState(
    wrapOpeningAckBody(
      `<p>Dear {{requester_name}},</p><p>This is a follow-up on ticket <strong>{{ticket_number}}</strong>: {{subject}}.</p><p>Please reply if you still need assistance.</p><p>Best regards,<br>Ban Teck Han Support</p>`,
    ),
  );
  const [followUpActive, setFollowUpActive] = useState(false);
  const [signatures, setSignatures] = useState<ReplySignature[]>([]);
  const [editingSigSlug, setEditingSigSlug] = useState<string | null>(null);
  const [signatureName, setSignatureName] = useState('');
  const [signatureBody, setSignatureBody] = useState('');
  const [signatureActive, setSignatureActive] = useState(true);
  const [addMailboxOpen, setAddMailboxOpen] = useState(false);
  const [mailboxAddress, setMailboxAddress] = useState('');
  const [mailboxName, setMailboxName] = useState('');
  const [mailboxPortal, setMailboxPortal] = useState<TicketPortalKind>('crm');
  const [tagName, setTagName] = useState('');
  const [tagColor, setTagColor] = useState('#6b7280');
  const [statusName, setStatusName] = useState('');
  const [statusSlug, setStatusSlug] = useState('');
  const [statusColor, setStatusColor] = useState('#6b7280');
  const [statusClosed, setStatusClosed] = useState(false);
  const [spamPattern, setSpamPattern] = useState('');
  const [spamRuleType, setSpamRuleType] = useState<TicketSpamRule['rule_type']>('subject');
  const [spamAction, setSpamAction] = useState<'spam' | 'block'>('spam');
  const [ackSubject, setAckSubject] = useState('[{{ticket_number}}] Your ticket has been created');
  const [ackBody, setAckBody] = useState('');
  const [ackActive, setAckActive] = useState(true);
  const [numberPreview, setNumberPreview] = useState('BTH-ENQ-2608-0001');
  const [autoReopenOnInbound, setAutoReopenOnInbound] = useState(true);
  const [followUpWorkdays, setFollowUpWorkdays] = useState(2);
  const [busy, setBusy] = useState(false);

  const loadMailboxes = useCallback(async () => {
    setError(null);
    try {
      const [mb, numberCfg] = await Promise.all([
        fetchAllTicketMailboxes(),
        fetchTicketNumberConfig().catch(() => null),
      ]);
      setMailboxes(mb);
      if (numberCfg?.preview) setNumberPreview(numberCfg.preview);
      setSelectedMailbox((prev) => {
        if (!prev) return null;
        return mb.find((m) => m.id === prev.id) ?? null;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load mailboxes.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMailboxConfig = useCallback(async (mailboxId: string) => {
    setError(null);
    setConfigLoading(true);
    try {
      const [tagRows, statusRows, rules, ackTpl, closingTpl, followTpl, sigList, workflow] =
        await Promise.all([
          fetchAllTicketTagDefinitions(mailboxId),
          fetchAllTicketStatusDefinitions(mailboxId),
          fetchSpamRules(mailboxId),
          fetchTicketEmailTemplate('opening_ack', mailboxId),
          fetchTicketEmailTemplate('closing_ack', mailboxId),
          fetchTicketEmailTemplate('follow_up_ack', mailboxId),
          listReplySignatures(mailboxId),
          fetchTicketWorkflowSettings(mailboxId),
        ]);
      setTags(tagRows);
      setStatuses(statusRows);
      setSpamRules(rules);
      if (ackTpl) {
        setAckSubject(ackTpl.subject_template.replace(/^\[\[/, '[').replace(/\]\]/, ']'));
        setAckBody(toStoredOpeningAckBody(ackTpl.body_html_template));
        setAckActive(ackTpl.is_active);
      } else {
        setAckSubject('[{{ticket_number}}] Your ticket has been created');
        setAckBody('');
        setAckActive(true);
      }
      if (closingTpl) {
        setClosingSubject(closingTpl.subject_template.replace(/^\[\[/, '[').replace(/\]\]/, ']'));
        setClosingBody(toStoredOpeningAckBody(closingTpl.body_html_template));
        setClosingActive(closingTpl.is_active);
      } else {
        setClosingSubject('[{{ticket_number}}] Your ticket has been closed');
        setClosingBody(wrapOpeningAckBody(DEFAULT_CLOSING_ACK_INNER));
        setClosingActive(false);
      }
      if (followTpl) {
        setFollowUpSubject(followTpl.subject_template.replace(/^\[\[/, '[').replace(/\]\]/, ']'));
        setFollowUpBody(toStoredOpeningAckBody(followTpl.body_html_template));
        setFollowUpActive(followTpl.is_active);
      } else {
        setFollowUpSubject('Follow-up: {{subject}} [{{ticket_number}}]');
        setFollowUpBody(
          wrapOpeningAckBody(
            `<p>Dear {{requester_name}},</p><p>This is a follow-up on ticket <strong>{{ticket_number}}</strong>: {{subject}}.</p><p>Please reply if you still need assistance.</p><p>Best regards,<br>Ban Teck Han Support</p>`,
          ),
        );
        setFollowUpActive(false);
      }
      setSignatures(sigList);
      setSignatureActive(sigList.some((s) => s.isActive));
      setAutoReopenOnInbound(workflow.auto_reopen_on_inbound);
      setFollowUpWorkdays(workflow.follow_up_workdays ?? 2);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load mailbox settings.');
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMailboxes();
  }, [loadMailboxes]);

  useEffect(() => {
    if (!selectedMailbox) return;
    void loadMailboxConfig(selectedMailbox.id);
  }, [selectedMailbox?.id, loadMailboxConfig]);

  useEffect(() => {
    if (!message) return;
    const t = window.setTimeout(() => setMessage(null), 4000);
    return () => window.clearTimeout(t);
  }, [message]);

  async function toggleActive(mb: TicketMailbox, next: boolean) {
    setBusy(true);
    setError(null);
    try {
      await upsertTicketMailbox({
        id: mb.id,
        address: mb.address,
        display_name: mb.display_name,
        is_active: next,
        portal_kind: mb.portal_kind,
      });
      setMessage(`${mb.address} ${next ? 'enabled' : 'disabled'}.`);
      await loadMailboxes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed.');
    } finally {
      setBusy(false);
    }
  }

  async function removeMailbox(mb: TicketMailbox) {
    if (
      !window.confirm(
        `Delete mailbox ${mb.address}?\n\nExisting tickets stay; they will just be unlinked from this mailbox.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await deleteTicketMailbox(mb.id);
      if (selectedMailbox?.id === mb.id) {
        setSelectedMailbox(null);
      }
      setMessage(`Deleted ${mb.address}.`);
      await loadMailboxes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
    } finally {
      setBusy(false);
    }
  }

  function openAddMailbox() {
    setMailboxPortal('crm');
    setMailboxAddress('');
    setMailboxName('');
    setError(null);
    setAddMailboxOpen(true);
  }

  function closeAddMailbox() {
    setAddMailboxOpen(false);
    setMailboxAddress('');
    setMailboxName('');
    setMailboxPortal('crm');
  }

  function toggleMailboxExpanded(mb: TicketMailbox) {
    if (selectedMailbox?.id === mb.id) {
      setSelectedMailbox(null);
      setManageOpen(null);
      setTemplateKind(null);
      setEditingSigSlug(null);
      return;
    }
    setManageOpen(null);
    setTemplateKind(null);
    setEditingSigSlug(null);
    setSelectedMailbox(mb);
  }

  async function addMailbox() {
    if (!mailboxAddress.includes('@')) return;
    const displayName =
      mailboxName.trim() || (mailboxPortal === 'it' ? 'IT Support' : 'Customer Support');
    setBusy(true);
    setError(null);
    try {
      await upsertTicketMailbox({
        address: mailboxAddress.trim(),
        display_name: displayName,
        is_active: false,
        portal_kind: mailboxPortal,
      });
      closeAddMailbox();
      setMessage(
        `Mailbox added for ${mailboxPortal === 'it' ? 'IT Management' : 'CRM'} (inactive). Enable after Graph authorization.`,
      );
      await loadMailboxes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Add mailbox failed.');
    } finally {
      setBusy(false);
    }
  }

  async function addTag() {
    if (!tagName.trim() || !selectedMailbox) return;
    setBusy(true);
    setError(null);
    try {
      await upsertTicketTagDefinition({
        mailbox_id: selectedMailbox.id,
        name: tagName.trim(),
        color: tagColor,
      });
      setTagName('');
      setMessage(`Tag "${tagName.trim()}" saved.`);
      await loadMailboxConfig(selectedMailbox.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Add tag failed.');
    } finally {
      setBusy(false);
    }
  }

  async function removeTag(tag: TicketTagDefinition) {
    if (!window.confirm(`Delete tag "${tag.name}"?`) || !selectedMailbox) return;
    setBusy(true);
    setError(null);
    try {
      await deleteTicketTagDefinition(tag.id);
      setMessage(`Deleted tag "${tag.name}".`);
      await loadMailboxConfig(selectedMailbox.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete tag failed.');
    } finally {
      setBusy(false);
    }
  }

  async function addStatus() {
    if (!statusName.trim() || !selectedMailbox) return;
    setBusy(true);
    setError(null);
    try {
      await upsertTicketStatusDefinition({
        mailbox_id: selectedMailbox.id,
        slug: statusSlug || statusName,
        name: statusName.trim(),
        color: statusColor,
        is_closed: statusClosed,
      });
      setStatusName('');
      setStatusSlug('');
      setStatusClosed(false);
      setMessage(`Status "${statusName.trim()}" saved.`);
      await loadMailboxConfig(selectedMailbox.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Add status failed.');
    } finally {
      setBusy(false);
    }
  }

  async function removeStatus(row: TicketStatusDefinition) {
    if (row.is_system) {
      setError('System statuses cannot be deleted.');
      return;
    }
    if (!window.confirm(`Delete status "${row.name}"?`) || !selectedMailbox) return;
    setBusy(true);
    setError(null);
    try {
      await deleteTicketStatusDefinition(row.id);
      setMessage(`Deleted status "${row.name}".`);
      await loadMailboxConfig(selectedMailbox.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
    } finally {
      setBusy(false);
    }
  }

  async function addSpamRule() {
    if (!spamPattern.trim() || !selectedMailbox) return;
    setBusy(true);
    setError(null);
    try {
      await upsertSpamRule({
        mailbox_id: selectedMailbox.id,
        rule_type: spamRuleType,
        pattern: spamPattern.trim(),
        action: spamAction,
        created_by_email: '',
      });
      setSpamPattern('');
      setMessage('Spam rule added.');
      await loadMailboxConfig(selectedMailbox.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Add rule failed.');
    } finally {
      setBusy(false);
    }
  }

  async function toggleSpamRule(rule: TicketSpamRule) {
    if (!selectedMailbox) return;
    setBusy(true);
    setError(null);
    try {
      await setSpamRuleActive(rule.id, !rule.is_active);
      await loadMailboxConfig(selectedMailbox.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed.');
    } finally {
      setBusy(false);
    }
  }

  async function saveTemplate(
    slug: 'opening_ack' | 'closing_ack' | 'follow_up_ack',
    extra?: { is_active?: boolean },
  ) {
    if (!selectedMailbox) return;
    setBusy(true);
    setError(null);
    try {
      if (slug === 'opening_ack') {
        await upsertTicketEmailTemplate({
          mailbox_id: selectedMailbox.id,
          slug,
          name: 'Opening acknowledgement',
          subject_template: ackSubject.trim(),
          body_html_template: toStoredOpeningAckBody(ackBody),
          is_active: extra?.is_active ?? ackActive,
        });
        if (extra?.is_active != null) setAckActive(extra.is_active);
        setMessage('Opening acknowledgement saved.');
      } else if (slug === 'closing_ack') {
        await upsertTicketEmailTemplate({
          mailbox_id: selectedMailbox.id,
          slug,
          name: 'Closing acknowledgement',
          subject_template: closingSubject.trim(),
          body_html_template: toStoredOpeningAckBody(closingBody),
          is_active: extra?.is_active ?? closingActive,
        });
        if (extra?.is_active != null) setClosingActive(extra.is_active);
        setMessage('Closing acknowledgement saved.');
      } else {
        await upsertTicketEmailTemplate({
          mailbox_id: selectedMailbox.id,
          slug,
          name: 'Follow-up reminder',
          subject_template: followUpSubject.trim(),
          body_html_template: toStoredOpeningAckBody(followUpBody),
          is_active: extra?.is_active ?? followUpActive,
        });
        if (extra?.is_active != null) setFollowUpActive(extra.is_active);
        setMessage('Follow-up template saved.');
      }
      await loadMailboxConfig(selectedMailbox.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save template failed.');
    } finally {
      setBusy(false);
    }
  }

  function openNewSignature() {
    setEditingSigSlug('__new__');
    setSignatureName('Signature');
    setSignatureBody('');
    setSignatureActive(true);
  }

  function openEditSignature(sig: ReplySignature) {
    setEditingSigSlug(sig.slug);
    setSignatureName(sig.name);
    setSignatureBody(sig.html);
    setSignatureActive(sig.isActive);
  }

  async function saveSignatureEditor() {
    if (!selectedMailbox) return;
    setBusy(true);
    setError(null);
    try {
      await saveReplySignature({
        mailbox_id: selectedMailbox.id,
        slug: editingSigSlug === '__new__' ? undefined : editingSigSlug ?? undefined,
        name: signatureName,
        html: signatureBody,
        isActive: signatureActive,
      });
      setMessage('Signature saved.');
      setEditingSigSlug(null);
      await loadMailboxConfig(selectedMailbox.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save signature failed.');
    } finally {
      setBusy(false);
    }
  }

  async function removeSignature(sig: ReplySignature) {
    if (!window.confirm(`Delete signature "${sig.name}"?`) || !selectedMailbox) return;
    setBusy(true);
    setError(null);
    try {
      await deleteReplySignature(sig.slug, selectedMailbox.id);
      setMessage('Signature deleted.');
      await loadMailboxConfig(selectedMailbox.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
    } finally {
      setBusy(false);
    }
  }

  async function removeSpamRule(rule: TicketSpamRule) {
    if (!window.confirm(`Delete rule "${rule.pattern}"?`) || !selectedMailbox) return;
    setBusy(true);
    setError(null);
    try {
      await deleteSpamRule(rule.id);
      setMessage('Spam rule deleted.');
      await loadMailboxConfig(selectedMailbox.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
    } finally {
      setBusy(false);
    }
  }

  const activeCount = mailboxes.filter((m) => m.is_active).length;

  const notifyRows = [
    {
      slug: 'opening_ack' as const,
      title: 'Receiving a new ticket',
      hint: 'Opening acknowledgement to the requester',
      active: ackActive,
    },
    {
      slug: 'closing_ack' as const,
      title: 'Closing a ticket',
      hint: 'Optional — you are asked each time you close a ticket',
      active: closingActive,
    },
    {
      slug: 'follow_up_ack' as const,
      title: 'Follow-up ticket',
      hint: 'Manual follow-up email template (never auto-sent). Idle tickets only remind agents in the UI.',
      active: followUpActive,
    },
  ];

  const headActions = (
    <div className="ticketing-admin__head-actions">
      <span className="settings-panel__count">
        {mailboxes.length} mailbox{mailboxes.length === 1 ? '' : 'es'}
        {mailboxes.length > 0 ? ` · ${activeCount} active` : ''}
      </span>
      <button
        type="button"
        className="btn-primary btn-sm"
        disabled={busy}
        onClick={openAddMailbox}
      >
        Add mailbox
      </button>
    </div>
  );

  const mailboxBody = (
    <>
      {error ? <div className="alert alert-error ticketing-admin__banner">{error}</div> : null}
      {message ? <div className="alert alert-success ticketing-admin__banner">{message}</div> : null}
      {loading ? (
        <p className="settings-empty">Loading mailboxes…</p>
      ) : mailboxes.length === 0 ? (
        <p className="settings-empty">No mailboxes yet.</p>
      ) : (
        <div className="ticket-settings-rows ticket-mailbox-rows">
          {mailboxes.map((mb) => {
            const expanded = selectedMailbox?.id === mb.id;
            return (
              <div
                key={mb.id}
                className={`ticket-mailbox-item${expanded ? ' ticket-mailbox-item--expanded' : ''}`}
              >
                <div
                  className="ticket-settings-row ticket-mailbox-item__header"
                  role="button"
                  tabIndex={0}
                  aria-expanded={expanded}
                  onClick={() => toggleMailboxExpanded(mb)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleMailboxExpanded(mb);
                    }
                  }}
                >
                    <div>
                      <p className="ticket-settings-row__title">
                        <span
                          className={`ticket-mailbox-portal-badge ticket-mailbox-portal-badge--${mb.portal_kind === 'it' ? 'it' : 'crm'}`}
                        >
                          {mb.portal_kind === 'it' ? 'IT' : 'CRM'}
                        </span>
                        {mb.display_name || mb.address}
                      </p>
                      <p className="ticket-settings-row__hint">{mb.address}</p>
                    </div>
                    <div
                      className="ticket-settings-row__actions"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <label className="ticketing-admin__switch">
                        <input
                          type="checkbox"
                          checked={mb.is_active}
                          disabled={busy}
                          onChange={(e) => void toggleActive(mb, e.target.checked)}
                        />
                        <span className="ticketing-admin__switch-ui" aria-hidden />
                      </label>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        disabled={busy}
                        onClick={() => void removeMailbox(mb)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  {expanded ? (
                    <div className="ticket-mailbox-item__body">
                      {configLoading ? (
                        <p className="settings-empty">Loading mailbox settings…</p>
                      ) : (
                        <>
                          <section className="ticket-mailbox-setup">
                            <div className="settings-panel__head">
                              <div>
                                <h2 className="settings-panel__title">Ticket setup</h2>
                                <p className="settings-panel__lead">
                                  Tags, statuses, and spam rules for this mailbox only.
                                </p>
                              </div>
                            </div>
                            <div className="ticket-settings-rows">
                              {(
                                [
                                  ['tags', 'Ticket tags', `${tags.filter((t) => t.is_active).length} active`] as const,
                                  [
                                    'statuses',
                                    'Ticket statuses',
                                    `${statuses.filter((s) => s.is_active).length} active`,
                                  ] as const,
                                  [
                                    'spam',
                                    'Spam & block rules',
                                    `${spamRules.length} rule${spamRules.length === 1 ? '' : 's'}`,
                                  ] as const,
                                ]
                              ).map(([id, title, count]) => (
                                <div key={id} className="ticket-settings-row">
                                  <p className="ticket-settings-row__title">{title}</p>
                                  <div className="ticket-settings-row__actions">
                                    <span className="settings-panel__count">{count}</span>
                                    <button
                                      type="button"
                                      className="btn-secondary btn-sm"
                                      onClick={() => setManageOpen(id)}
                                    >
                                      Manage
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </section>

                          <section className="ticket-mailbox-setup">
                            <div className="settings-panel__head">
                              <div>
                                <h2 className="settings-panel__title">Email notifications</h2>
                                <p className="settings-panel__lead">
                                  Toggle each notification, then use the pencil to edit the template.
                                </p>
                              </div>
                            </div>
                            <div className="ticket-settings-rows">
                              {notifyRows.map((row) => (
                                <div key={row.slug} className="ticket-settings-row">
                                  <div>
                                    <p className="ticket-settings-row__title">{row.title}</p>
                                    <p className="ticket-settings-row__hint">{row.hint}</p>
                                  </div>
                                  <div className="ticket-settings-row__actions">
                                    <label className="ticketing-admin__switch">
                                      <input
                                        type="checkbox"
                                        checked={row.active}
                                        disabled={busy}
                                        onChange={(e) => void saveTemplate(row.slug, { is_active: e.target.checked })}
                                      />
                                      <span className="ticketing-admin__switch-ui" aria-hidden />
                                    </label>
                                    <button
                                      type="button"
                                      className="ticket-settings-row__pencil"
                                      title="Email template"
                                      aria-label={`Edit ${row.title} template`}
                                      onClick={() => setTemplateKind(row.slug)}
                                    >
                                      ✎
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </section>

                          <section className="ticket-mailbox-setup">
                            <div className="settings-panel__head">
                              <div>
                                <h2 className="settings-panel__title">Reply signatures</h2>
                                <p className="settings-panel__lead">
                                  Create multiple signatures. Agents pick one when replying — signatures cannot be
                                  edited in Reply.
                                </p>
                              </div>
                              <button
                                type="button"
                                className="btn-primary btn-sm"
                                disabled={busy}
                                onClick={openNewSignature}
                              >
                                Add signature
                              </button>
                            </div>
                            {signatures.length === 0 ? (
                              <p className="settings-empty">No signatures yet.</p>
                            ) : (
                              <div className="ticket-settings-rows">
                                {signatures.map((sig) => (
                                  <div key={sig.slug} className="ticket-settings-row">
                                    <div>
                                      <p className="ticket-settings-row__title">
                                        {sig.name}
                                        {sig.isDefault ? ' · Default' : ''}
                                      </p>
                                      <p className="ticket-settings-row__hint">
                                        {sig.isActive ? 'Active' : 'Inactive'}
                                      </p>
                                    </div>
                                    <div className="ticket-settings-row__actions">
                                      <button
                                        type="button"
                                        className="btn-secondary btn-sm"
                                        disabled={busy}
                                        onClick={() => openEditSignature(sig)}
                                      >
                                        Edit
                                      </button>
                                      {!sig.isDefault ? (
                                        <button
                                          type="button"
                                          className="btn-secondary btn-sm"
                                          disabled={busy}
                                          onClick={() => void removeSignature(sig)}
                                        >
                                          Delete
                                        </button>
                                      ) : null}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </section>

                        </>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
    </>
  );

  const mailboxModals = (
    <>
      <Modal open={addMailboxOpen} onClose={closeAddMailbox} title="Add mailbox">
        <div className="settings-add-form ticketing-admin__add-form ticketing-admin__add-form--modal">
          <label className="field">
            <span className="field-label">Portal</span>
            <select
              className="field-input"
              value={mailboxPortal}
              onChange={(e) => {
                const next = e.target.value === 'it' ? 'it' : 'crm';
                setMailboxPortal(next);
              }}
              disabled={busy}
            >
              <option value="crm">CRM (Sales)</option>
              <option value="it">IT Management</option>
            </select>
          </label>
          <label className="field">
            <span className="field-label">Email address</span>
            <input
              className="field-input"
              type="email"
              placeholder="name@company.com"
              value={mailboxAddress}
              onChange={(e) => setMailboxAddress(e.target.value)}
              disabled={busy}
              autoFocus
            />
          </label>
          <label className="field">
            <span className="field-label">Display name</span>
            <input
              className="field-input"
              placeholder="Customer Support"
              value={mailboxName}
              onChange={(e) => setMailboxName(e.target.value)}
              disabled={busy}
            />
          </label>
          <div className="ticketing-admin__add-actions">
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={busy}
              onClick={closeAddMailbox}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={busy || !mailboxAddress.includes('@')}
              onClick={() => void addMailbox()}
            >
              {busy ? 'Saving…' : 'Add mailbox'}
            </button>
          </div>
        </div>
      </Modal>

        <Modal open={manageOpen === 'tags'} onClose={() => setManageOpen(null)} title="Ticket tags" wide>
          <div className="settings-add-form ticketing-admin__add-form" style={{ marginBottom: '0.75rem' }}>
            <label className="field">
              <span className="field-label">Name</span>
              <input
                className="field-input"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                disabled={busy}
              />
            </label>
            <label className="field">
              <span className="field-label">Color</span>
              <input
                className="field-input"
                type="color"
                value={tagColor}
                onChange={(e) => setTagColor(e.target.value)}
                disabled={busy}
              />
            </label>
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={busy || !tagName.trim()}
              onClick={() => void addTag()}
            >
              Add
            </button>
          </div>
          {tags.length === 0 ? (
            <p className="settings-empty">No tags yet.</p>
          ) : (
            <div className="ticket-settings-rows">
              {tags.map((tag) => (
                <div key={tag.id} className="ticket-settings-row">
                  <p className="ticket-settings-row__title">
                    <span className="ticket-settings-row__dot" style={{ background: tag.color }} />
                    {tag.name}
                  </p>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={busy}
                    onClick={() => void removeTag(tag)}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </Modal>

        <Modal
          open={manageOpen === 'statuses'}
          onClose={() => setManageOpen(null)}
          title="Ticket statuses"
          wide
        >
          <label className="ticketing-admin__switch" style={{ marginBottom: '0.75rem' }}>
            <input
              type="checkbox"
              checked={autoReopenOnInbound}
              disabled={busy}
              onChange={(e) => {
                const next = e.target.checked;
                if (!selectedMailbox) return;
                setAutoReopenOnInbound(next);
                setBusy(true);
                void saveTicketWorkflowSettings(
                  {
                    auto_reopen_on_inbound: next,
                    follow_up_workdays: followUpWorkdays,
                  },
                  selectedMailbox.id,
                )
                  .then(() => setMessage(next ? 'Auto-reopen enabled.' : 'Auto-reopen disabled.'))
                  .catch((err) =>
                    setError(err instanceof Error ? err.message : 'Failed to save workflow setting.'),
                  )
                  .finally(() => setBusy(false));
              }}
            />
            <span className="ticketing-admin__switch-ui" aria-hidden />
            <span className="ticketing-admin__switch-label">
              Auto-reopen Closed / On hold when the customer emails
            </span>
          </label>
          <label className="field" style={{ marginBottom: '0.75rem', display: 'block', maxWidth: 280 }}>
            <span className="field-label">Follow-up every N working days</span>
            <input
              className="field-input"
              type="number"
              min={0}
              max={30}
              value={followUpWorkdays}
              disabled={busy}
              onChange={(e) => setFollowUpWorkdays(Number(e.target.value) || 0)}
              onBlur={() => {
                if (!selectedMailbox) return;
                setBusy(true);
                void saveTicketWorkflowSettings(
                  {
                    auto_reopen_on_inbound: autoReopenOnInbound,
                    follow_up_workdays: followUpWorkdays,
                  },
                  selectedMailbox.id,
                )
                  .then((saved) => {
                    setFollowUpWorkdays(saved.follow_up_workdays);
                    setMessage(
                      saved.follow_up_workdays > 0
                        ? `Follow-up every ${saved.follow_up_workdays} working day(s).`
                        : 'Follow-up reminders disabled.',
                    );
                  })
                  .catch((err) =>
                    setError(
                      err instanceof Error ? err.message : 'Failed to save follow-up setting.',
                    ),
                  )
                  .finally(() => setBusy(false));
              }}
            />
            <span className="field-hint">
              0 = off. Shows an in-app reminder for agents when a ticket is idle (Mon–Fri). Does not
              auto-send email — use Email notifications → Follow-up ticket for the optional manual
              template.
            </span>
          </label>
          <div className="settings-add-form ticketing-admin__add-form" style={{ marginBottom: '0.75rem' }}>
            <label className="field">
              <span className="field-label">Name</span>
              <input
                className="field-input"
                value={statusName}
                onChange={(e) => setStatusName(e.target.value)}
                disabled={busy}
              />
            </label>
            <label className="field">
              <span className="field-label">Color</span>
              <input
                className="field-input"
                type="color"
                value={statusColor}
                onChange={(e) => setStatusColor(e.target.value)}
                disabled={busy}
              />
            </label>
            <label className="ticketing-admin__switch ticketing-admin__switch--inline">
              <input
                type="checkbox"
                checked={statusClosed}
                onChange={(e) => setStatusClosed(e.target.checked)}
                disabled={busy}
              />
              <span className="ticketing-admin__switch-label">Closed</span>
            </label>
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={busy || !statusName.trim()}
              onClick={() => void addStatus()}
            >
              Add
            </button>
          </div>
          <div className="ticket-settings-rows">
            {statuses.map((row) => (
              <div key={row.id} className="ticket-settings-row">
                <p className="ticket-settings-row__title">
                  <span className="ticket-settings-row__dot" style={{ background: row.color }} />
                  {row.name}
                  {row.is_system ? ' · System' : ''}
                </p>
                {!row.is_system ? (
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={busy}
                    onClick={() => void removeStatus(row)}
                  >
                    Delete
                  </button>
                ) : (
                  <span className="ticket-settings-row__hint">{row.slug}</span>
                )}
              </div>
            ))}
          </div>
        </Modal>

        <Modal open={manageOpen === 'spam'} onClose={() => setManageOpen(null)} title="Spam & block rules" wide>
          <p className="settings-panel__lead" style={{ marginTop: 0 }}>
            Job applications rotate Gmail addresses — match subject or body text instead of one sender.
          </p>
          <div className="settings-add-form ticketing-admin__add-form" style={{ marginBottom: '0.75rem' }}>
            <label className="field">
              <span className="field-label">Type</span>
              <select
                className="field-input"
                value={spamRuleType}
                onChange={(e) => setSpamRuleType(e.target.value as typeof spamRuleType)}
                disabled={busy}
              >
                <option value="email">Email</option>
                <option value="domain">Domain</option>
                <option value="subject">Subject</option>
                <option value="body">Body</option>
              </select>
            </label>
            <label className="field">
              <span className="field-label">Pattern</span>
              <input
                className="field-input"
                value={spamPattern}
                onChange={(e) => setSpamPattern(e.target.value)}
                disabled={busy}
              />
            </label>
            <label className="field">
              <span className="field-label">Action</span>
              <select
                className="field-input"
                value={spamAction}
                onChange={(e) => setSpamAction(e.target.value as typeof spamAction)}
                disabled={busy}
              >
                <option value="spam">Mark as spam</option>
                <option value="block">Block</option>
              </select>
            </label>
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={busy || !spamPattern.trim()}
              onClick={() => void addSpamRule()}
            >
              Add
            </button>
          </div>
          <div className="ticket-settings-rows">
            {spamRules.map((rule) => (
              <div key={rule.id} className="ticket-settings-row">
                <div>
                  <p className="ticket-settings-row__title">
                    {rule.rule_type}: {rule.pattern}
                  </p>
                  <p className="ticket-settings-row__hint">{rule.action}</p>
                </div>
                <div className="ticket-settings-row__actions">
                  <label className="ticketing-admin__switch">
                    <input
                      type="checkbox"
                      checked={rule.is_active}
                      disabled={busy}
                      onChange={() => void toggleSpamRule(rule)}
                    />
                    <span className="ticketing-admin__switch-ui" aria-hidden />
                  </label>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={busy}
                    onClick={() => void removeSpamRule(rule)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Modal>

        <Modal
          open={Boolean(templateKind)}
          onClose={() => setTemplateKind(null)}
          title={
            templateKind === 'closing_ack'
              ? 'Email template · Closing a ticket'
              : templateKind === 'follow_up_ack'
                ? 'Email template · Follow-up ticket'
                : 'Email template · Receiving a new ticket'
          }
          extraWide
        >
          <div className="ticketing-admin__template">
            <OpeningAckTemplateEditor
              subject={
                templateKind === 'closing_ack'
                  ? closingSubject
                  : templateKind === 'follow_up_ack'
                    ? followUpSubject
                    : ackSubject
              }
              onSubjectChange={
                templateKind === 'closing_ack'
                  ? setClosingSubject
                  : templateKind === 'follow_up_ack'
                    ? setFollowUpSubject
                    : setAckSubject
              }
              bodyHtml={
                templateKind === 'closing_ack'
                  ? closingBody
                  : templateKind === 'follow_up_ack'
                    ? followUpBody
                    : ackBody
              }
              onBodyHtmlChange={
                templateKind === 'closing_ack'
                  ? setClosingBody
                  : templateKind === 'follow_up_ack'
                    ? setFollowUpBody
                    : setAckBody
              }
              disabled={busy}
              previewTicketNumber={numberPreview}
            />
            <div className="ticketing-admin__add-actions">
              <button
                type="button"
                className="btn-primary btn-sm"
                disabled={busy || !templateKind}
                onClick={() => {
                  if (templateKind) void saveTemplate(templateKind);
                }}
              >
                {busy ? 'Saving…' : 'Save template'}
              </button>
            </div>
          </div>
        </Modal>

        <Modal
          open={Boolean(editingSigSlug)}
          onClose={() => setEditingSigSlug(null)}
          title={editingSigSlug === '__new__' ? 'Add reply signature' : 'Edit reply signature'}
          extraWide
        >
          <div>
            <label className="field" style={{ marginBottom: '0.75rem', display: 'block' }}>
              <span className="field-label">Name</span>
              <input
                className="field-input"
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                disabled={busy}
                placeholder="e.g. Support EN / Sales"
              />
            </label>
            <label className="ticketing-admin__switch" style={{ marginBottom: '0.75rem' }}>
              <input
                type="checkbox"
                checked={signatureActive}
                disabled={busy}
                onChange={(e) => setSignatureActive(e.target.checked)}
              />
              <span className="ticketing-admin__switch-ui" aria-hidden />
              <span style={{ marginLeft: 8 }}>Active (shown in Reply)</span>
            </label>
            <p className="settings-panel__lead" style={{ marginTop: 0 }}>
              Images are allowed. Agents can only select this signature when replying — not edit it.
            </p>
            <Suspense fallback={<p className="settings-empty">Loading editor…</p>}>
              <MemoBodyEditor
                value={signatureBody}
                onChange={setSignatureBody}
                disabled={busy}
                emailFormat
                defaultEmailFont
                allowImages
                placeholder="Enter reply signature"
              />
            </Suspense>
            <div className="ticketing-admin__add-actions" style={{ marginTop: '0.75rem' }}>
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={busy}
                onClick={() => setEditingSigSlug(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary btn-sm"
                disabled={busy}
                onClick={() => void saveSignatureEditor()}
              >
                {busy ? 'Saving…' : 'Save signature'}
              </button>
            </div>
          </div>
        </Modal>
    </>
  );

  if (embedded) {
    return (
      <div className="ticketing-admin ticketing-admin--embedded">
        <div className="ticketing-admin__embedded-toolbar">{headActions}</div>
        {mailboxBody}
        {mailboxModals}
      </div>
    );
  }

  return (
    <div className="ticketing-admin">
      <section className="card settings-panel">
        <div className="settings-panel__head">
          <div>
            <h2 className="settings-panel__title">Mailboxes</h2>
          </div>
          {headActions}
        </div>
        {mailboxBody}
      </section>
      {mailboxModals}
    </div>
  );
}
